/**
 * Defines an object that stores the identities of document container groups along with their related properties.
 *
 * @module MW/layout/DocumentGroupRegistry
 * @copyright Copyright 2016 The MathWorks, Inc.
 */
define([
    "dojo/_base/declare",
    "dojo/Evented"
], function (declare, Evented) {
    var DocumentGroupRegistry = declare([Evented], {

        // Stores groups by facet and then by id

        /**
         * @instance
         */
        constructor: function () {
            this._groups = {};
        },

        /**
         * Registers a group's properties
         *
         * @param facet {String} the facet of the document which determines the group being registered.  More specifically
         *                       this is the object key within the document's properties under which the group id is stored.
         * @param id {String} identifies the group.  Optional: If not specified the facet is registered without any groups.
         * @param properties {DocumentGroupProperties) the group properties to be stored.  Optional: Not used if id isn't specified
         * @instance
         */
        register: function (facet, id, properties) {
            if (!this._groups[facet]) {
                this._groups[facet] = {};
            }
            if (id) {
                this._groups[facet][id] = properties;
                properties.groupId = id;
                this.emit(DocumentGroupRegistry.PROPERTIES_ADDED, facet, id, properties);
            }
        },

        /**
         * Removes a group's properties from the registry
         *
         * @param facet {String} the facet associated with the group
         * @param id {String} the group identifier.  Optional: If not specified then the facet will be removed from the registry
         *           Facets are not automatically removed when they become empty, i.e. have no registered group ids.
         * @instance
         */
        unregister: function (facet, id) {
            if (this._groups[facet]) {
                if (id) {
                    this.emit(DocumentGroupRegistry.PROPERTIES_REMOVED, facet, id, this._groups[facet][id]);
                    delete this._groups[facet][id];
                } else {
                    delete this._groups[facet];
                }
            }
        },

        /**
         * @returns {Array} Contains all of the facet that have been registered
         * @instance
         */
        getFacets: function () {
            return Object.keys(this._groups);
        },

        /**
         * @param facet {String} The facet whose ids are to be returned
         * @returns {Array} Contains all of the group ids registered under the facet
         * @instance
         */
        getIds: function (facet) {
            if (this._groups[facet]) {
                return Object.keys(this._groups[facet]);
            } else {
                return [];
            }
        },

        /**
         *
         * @param facet {String} the facet associated with the group of interest
         * @param id {String} identifies the group of interest.  If not specified an object containing all
         *        properties, keyed by groupId is returned
         * @returns {DocumentGroupProperties | Object}
         * @instance
         */
        getProperties: function (facet, id) {
            if (!this._groups[facet]) {
                this.register(facet);
            }
            if (id) {
                return this._groups[facet][id];
            } else {
                return this._groups[facet];
            }
        },

        /**
         * @param facet {String} the facet associated with the group of interest
         * @param documentProperties {Object} the documentProperties which store the group id under the given facet
         * @returns {DocumentGroupProperties} The group properties corresponding to the given facet
         * @instance
         */
        getGroupPropertiesFromDocumentProperties: function (facet, documentProperties) {
            if (this._groups[facet] && documentProperties[facet]) {
                return this._groups[facet][documentProperties[facet]];
            }
            return undefined;
        }
    });

    DocumentGroupRegistry.PROPERTIES_ADDED = "propertiesAdded";
    DocumentGroupRegistry.PROPERTIES_REMOVED = "propertiesRemoved";

    return DocumentGroupRegistry;
});